import React from "react";
import { Link } from "react-router-dom";
import "../../Styles/ann-tokens.css";
import "../../Styles/marketing-base.css";
import "../../Styles/subpage.css";
import "../../Styles/pages/why-ann.css";
import useReveal from "./useReveal";

export default function WhyAnn() {
  const ref = useReveal();

  return (
    <div className="page ann-subpage" ref={ref}>
      {/* HERO */}
      <section className="sub-hero" id="top">
        <div className="frame">
          <Link to="/" className="back">← Ann · Hiring intelligence</Link>
          <span>Why Ann</span>
        </div>
        <span className="eye">The case for hiring intelligence</span>
        <h1>
          Hiring was never a <em>search</em> problem<span className="period" />
        </h1>
        <p className="lede-xl">
          For thirty years, hiring software did one thing:{" "}
          <b>move candidates through a funnel faster</b>. The best candidate you
          never saw stayed exactly where they were — <em>invisible</em>. Ann does
          something different: it <em>understands</em> the role and the people in
          it. Not a faster funnel — a new category.
        </p>
        <div className="ctas">
          <a href="#define" className="btn btn-primary btn-lg">What that means</a>
          <Link to="/how-it-works" className="btn btn-secondary btn-lg">How Ann works</Link>
        </div>
      </section>

      {/* THE DEFINITION */}
      <section className="define" id="define">
        <div className="inner">
          <span className="eye">The category</span>
          <p className="def">
            <span className="term">Hiring intelligence</span> is software that
            understands a role the way an experienced recruiter would — then{" "}
            <b>finds, reads, and evaluates candidates on evidence of what they can
            actually do</b>, and explains every judgement it makes.
          </p>
          <p className="foot">
            // Not an applicant tracking system, which stores candidates. Not a
            sourcing tool, which finds more. It's the layer of judgement between
            the candidates and the decision — the part software has never done.
          </p>
        </div>
      </section>

      {/* THE PROBLEM WITH AUTOMATION */}
      <section className="sub-section">
        <div className="cols-split">
          <div className="sub-head" style={{ marginBottom: 0 }}>
            <span className="eye">The ceiling</span>
            <h2 className="sub-h2">Automation made a broken process <em>faster</em>.</h2>
          </div>
          <div className="prose">
            <p className="big">Every recruiting tool of the last three decades shares one assumption: that hiring is a pipeline, and the job is to move people through it quickly.</p>
            <p>So we got faster sourcing, faster screening, faster scheduling — and hiring got no better. The same well-written résumés rose; the same people who did the work but described it plainly got missed; the same interviews produced the same incomparable verdicts.</p>
            <p><b>Speed was never the bottleneck. Judgement was.</b> A funnel that runs twice as fast still surfaces the wrong people — just sooner.</p>
          </div>
        </div>
      </section>

      {/* THE SHIFT */}
      <section className="sub-section bone">
        <div className="sub-head">
          <span className="eye">The shift</span>
          <h2 className="sub-h2">From moving candidates to <em>understanding</em> them.</h2>
          <p className="lede">Three things change the moment software understands a role instead of just routing applications for it.</p>
        </div>
        <div className="shift-row">
          <div className="shift">
            <span className="n">01 · Sourcing</span>
            <span className="from">Match the keywords on the page.</span>
            <span className="to">Read for <em>capability</em> — including the people who never wrote the keyword.</span>
          </div>
          <div className="shift">
            <span className="n">02 · Evaluation</span>
            <span className="from">Reward the best-written résumé.</span>
            <span className="to">Score on <em>evidence of the work</em> — and cite it, every time.</span>
          </div>
          <div className="shift">
            <span className="n">03 · Decision</span>
            <span className="from">"Trust me, I just knew."</span>
            <span className="to">A verdict that comes with <em>the case behind it</em>, comparable across the slate.</span>
          </div>
        </div>
      </section>

      {/* UNDERSTANDS NOT AUTOMATES */}
      <section className="sub-section">
        <div className="cols-split">
          <div className="sub-head" style={{ marginBottom: 0 }}>
            <span className="eye">The principle</span>
            <h2 className="sub-h2">Ann <em>understands</em>. It doesn't just automate.</h2>
          </div>
          <div className="prose">
            <p className="big">Before Ann reads a single résumé, it reads three things a tool never bothered to: your company, the role, and the work the role really involves.</p>
            <p>A "Senior CS Manager" isn't a title — it's leading forty reps through a Q4 surge. A strong hire at your competitor might be wrong for how you build. The candidate who wrote "handled escalations" might be the best de-escalation lead on the slate. <b>Context is what lets Ann read everything else correctly.</b></p>
            <p>That's the line between the old category and the new one. Automation moves what you give it. <em>Intelligence understands what it's looking at.</em></p>
          </div>
        </div>
      </section>

      {/* WHAT ANN IS NOT */}
      <section className="sub-section bone">
        <div className="sub-head">
          <span className="eye">To be clear</span>
          <h2 className="sub-h2">What Ann is <em>not</em>.</h2>
          <p className="lede">A new category is easiest to understand by what it sits beside.</p>
        </div>
        <div className="cols-3 nots">
          <div className="point">
            <div className="x">Not <b>an ATS</b></div>
            <h3>It doesn't store candidates.</h3>
            <p>Your applicant tracking system records who applied and what stage they're at. Ann <em>judges</em> them — and writes the judgement back into your ATS. Different job, different layer.</p>
          </div>
          <div className="point">
            <div className="x">Not <b>a sourcing tool</b></div>
            <h3>It doesn't just find more.</h3>
            <p>Sourcing tools pour more résumés into the funnel. Ann finds the <em>right</em> people — including the ones a keyword search structurally misses — and tells you why they're right.</p>
          </div>
          <div className="point">
            <div className="x">Not <b>a general AI</b></div>
            <h3>It doesn't hand you an opinion.</h3>
            <p>A chatbot gives you a plausible paragraph. Ann gives you a structured, comparable, evidence-backed evaluation — the same format every time, defensible the day someone asks how you decided.</p>
          </div>
        </div>
      </section>

      {/* WHY NOW */}
      <section className="sub-section">
        <div className="cols-split">
          <div className="sub-head" style={{ marginBottom: 0 }}>
            <span className="eye">Why now</span>
            <h2 className="sub-h2">This wasn't <em>possible</em> until recently.</h2>
          </div>
          <div className="prose">
            <p className="big">Reading a résumé the way a careful human does — for what someone actually did, not the words they chose — is a hard problem. Until recently, software couldn't do it at all.</p>
            <p>So the industry matched keywords and counted years — every tool a workaround for the thing that couldn't be done. That constraint is gone. Software can now read for meaning, hold a role's real requirements in mind, and reason about evidence at a scale no human team could.</p>
            <p><b>Hiring intelligence is what you build once that constraint lifts.</b> Not a faster version of the old tools — the thing the old tools were standing in for all along.</p>
          </div>
        </div>
      </section>

      {/* CLOSING STATEMENT */}
      <section className="sub-section dark">
        <span className="eye" style={{ display: "block", marginBottom: 32 }}>The point</span>
        <p className="statement" style={{ maxWidth: "30ch" }}>
          Every recruiting tool automates hiring. Ann is the first that <em>understands</em> it.
        </p>
      </section>

      {/* CTA */}
      <section className="sub-cta">
        <span className="eye">See it on a real role</span>
        <h2>Stop routing. Start <em>understanding</em>.</h2>
        <p className="sub">Fifteen minutes, on a role off your own desk. See what hiring intelligence actually does before you change a thing.</p>
        <div className="ctas">
          <Link to="/contact-us" className="btn btn-primary btn-lg">Book a demo</Link>
          <Link to="/how-it-works" className="btn btn-secondary btn-lg">See how Ann works</Link>
        </div>
      </section>
    </div>
  );
}
